import * as THREE from "three";

/**
 * The BMX bike — a simple 3D prop built from primitives (like the fishing-rod prop), used both
 * PARKED in the world and attached under a rider's billboard. Built along +Z (the character
 * facing convention: forward = (sinθ, 0, cosθ)), origin at ground level between the wheels.
 * `tick` spins the wheels + cranks by the rider's speed so pedaling reads from every angle.
 */
export interface BmxProp {
  group: THREE.Group;
  /** Advance wheel/crank spin. `speed` in WORLD UNITS per second (0 = parked). */
  tick(dtMs: number, speed: number): void;
  dispose(): void;
}

const WHEEL_R = 0.26;

export function buildBmx(): BmxProp {
  const group = new THREE.Group();
  const frameMat = new THREE.MeshStandardMaterial({ color: 0xc23b3b, roughness: 0.55, metalness: 0.25 }); // BMX red
  const steelMat = new THREE.MeshStandardMaterial({ color: 0x2c2c30, roughness: 0.6, metalness: 0.4 });
  const tireMat = new THREE.MeshStandardMaterial({ color: 0x17171a, roughness: 0.95 });

  // ── wheels (torus tires + a spoke bar that visibly spins) ──
  const wheels: THREE.Group[] = [];
  for (const z of [0.42, -0.42]) {
    const w = new THREE.Group();
    const tire = new THREE.Mesh(new THREE.TorusGeometry(WHEEL_R, 0.035, 6, 14), tireMat);
    tire.rotation.y = Math.PI / 2; // tire plane along the travel direction
    w.add(tire);
    const spokes = new THREE.Mesh(new THREE.BoxGeometry(0.015, WHEEL_R * 1.8, 0.03), steelMat);
    w.add(spokes);
    const spokes2 = spokes.clone();
    spokes2.rotation.x = Math.PI / 2;
    w.add(spokes2);
    w.position.set(0, WHEEL_R + 0.02, z);
    group.add(w);
    wheels.push(w);
  }

  const tube = (from: THREE.Vector3, to: THREE.Vector3, r: number, mat: THREE.Material): void => {
    const d = new THREE.Vector3().subVectors(to, from);
    const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, d.length(), 5), mat);
    m.position.copy(from).addScaledVector(d, 0.5);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.clone().normalize());
    group.add(m);
  };

  const rear = new THREE.Vector3(0, WHEEL_R + 0.02, -0.42);
  const front = new THREE.Vector3(0, WHEEL_R + 0.02, 0.42);
  const crank = new THREE.Vector3(0, 0.24, 0.02);           // bottom bracket
  const seatTop = new THREE.Vector3(0, 0.62, -0.18);        // saddle
  const headTop = new THREE.Vector3(0, 0.70, 0.34);         // handlebar stem top

  // ── frame ──
  tube(rear, crank, 0.022, frameMat);                        // chain stay
  tube(crank, seatTop, 0.024, frameMat);                     // seat tube
  tube(rear, seatTop, 0.02, frameMat);                       // seat stay
  tube(crank, headTop, 0.026, frameMat);                     // down tube
  tube(seatTop, new THREE.Vector3(0, 0.66, 0.30), 0.022, frameMat); // top tube
  tube(front, headTop, 0.022, steelMat);                     // fork

  // ── saddle + handlebar ──
  const seat = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.045, 0.22), steelMat);
  seat.position.set(0, 0.66, -0.20);
  group.add(seat);
  const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.42, 5), steelMat);
  bar.rotation.z = Math.PI / 2;
  bar.position.set(0, 0.74, 0.34);
  group.add(bar);
  for (const x of [-0.19, 0.19]) { // grips
    const g = new THREE.Mesh(new THREE.CylinderGeometry(0.026, 0.026, 0.08, 5), tireMat);
    g.rotation.z = Math.PI / 2;
    g.position.set(x, 0.74, 0.34);
    group.add(g);
  }

  // ── cranks + pedals (spin with the wheels while pedaling) ──
  const crankArm = new THREE.Group();
  for (const side of [-1, 1]) {
    const arm = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.16, 0.03), steelMat);
    arm.position.set(side * 0.05, side * 0.06, 0);
    crankArm.add(arm);
    const pedal = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.02, 0.05), tireMat);
    pedal.position.set(side * 0.07, side * 0.13, 0);
    crankArm.add(pedal);
  }
  crankArm.position.copy(crank);
  group.add(crankArm);

  let spin = 0;
  return {
    group,
    tick(dtMs: number, speed: number): void {
      if (speed <= 0.01) return;
      spin += (speed / WHEEL_R) * (dtMs / 1000); // wheel angular velocity from ground speed
      for (const w of wheels) w.rotation.x = spin;
      crankArm.rotation.x = spin * 0.5; // cranks turn slower than the wheels
    },
    dispose(): void {
      group.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.geometry) m.geometry.dispose();
      });
      frameMat.dispose(); steelMat.dispose(); tireMat.dispose();
    },
  };
}
